import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import {
  ArrowLeft,
  MessageCircle,
  Pencil,
  Check,
  X,
  Trash2,
  Globe,
  Hash,
  User,
  Phone,
  Mail,
  Zap
} from 'lucide-react';

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  const usuario = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('usuario'));
    } catch { return null; }
  }, []);

  const canEdit = ['admin', 'superadmin'].includes(usuario?.rol);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await api.get(`/leads/${id}`);
        setLead(res.data);
      } catch (e) {
        console.error('Error cargando lead', e);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [id]);

  const saveEdit = async () => {
    try {
      const res = await api.put(`/leads/${id}`, { [editingField]: editValue });
      setLead(res.data);
      setEditingField(null);
    } catch (e) { console.error('Error guardando', e); }
  };

  const eliminarLead = async () => {
    if (!window.confirm('¿Eliminar este lead permanentemente?')) return;
    try {
      await api.delete(`/leads/${id}`);
      navigate('/leads');
    } catch (e) { alert('Error al eliminar'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-blue-600 animate-spin rounded-full" />
    </div>
  );

  if (!lead) return <div className="p-8 text-center text-slate-500 font-serif text-xl">Lead no encontrado</div>;

  const EditableRow = ({ label, field, icon: Icon, editable = true }) => {
    const isEditing = editingField === field;
    return (
      <div className="group flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl transition-colors">
        <div className="flex items-center gap-3 flex-1">
          <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-400">
            <Icon size={16} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase font-black tracking-widest text-slate-400">{label}</p>
            {isEditing ? (
              <input
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                className="w-full bg-white border border-blue-500 rounded px-2 py-0.5 text-sm focus:outline-none shadow-sm"
                autoFocus
                onKeyDown={(e) => e.key === 'Enter' && saveEdit()}
              />
            ) : (
              <p className="text-sm font-semibold text-slate-700">{lead[field] || '—'}</p>
            )}
          </div>
        </div>

        {canEdit && editable && (
          <div className="ml-4">
            {isEditing ? (
              <div className="flex items-center gap-2">
                <button onClick={saveEdit} className="p-1.5 bg-green-100 text-green-700 rounded-lg hover:bg-green-200">
                  <Check size={14} strokeWidth={3} />
                </button>
                <button onClick={() => setEditingField(null)} className="p-1.5 bg-slate-100 text-slate-500 rounded-lg">
                  <X size={14} strokeWidth={3} />
                </button>
              </div>
            ) : (
              <button
                onClick={() => { setEditingField(field); setEditValue(lead[field] || ''); }}
                className="p-1.5 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600 transition-all"
              >
                <Pencil size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/50 pb-20">
      {/* BARRA SUPERIOR NAV */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors">
            <ArrowLeft size={16} /> Volver
          </button>
          {canEdit && (
            <button onClick={eliminarLead} className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-red-500 hover:bg-red-50 px-4 py-2 rounded-xl transition-all">
              <Trash2 size={16} /> Eliminar
            </button>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 mt-10 grid md:grid-cols-3 gap-8">
        {/* COLUMNA IZQUIERDA: PERFIL */}
        <div className="md:col-span-2 space-y-6">
          <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm">
            <div className="flex items-start justify-between mb-8">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-[10px] font-black uppercase tracking-widest mb-4">
                  <Zap size={12} fill="currentColor" /> Lead Activo
                </span>
                <h1 className="text-4xl font-serif text-slate-900 leading-tight">
                  {lead.nombre || 'Sin nombre registrado'}
                </h1>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-black uppercase text-slate-400 mb-1">Estado actual</p>
                <p className="text-sm font-bold bg-slate-900 text-white px-4 py-1.5 rounded-full">{lead.estado_nombre || 'Nuevo'}</p>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-2">
              <EditableRow label="Nombre Completo" field="nombre" icon={User} />
              <EditableRow label="Teléfono" field="telefono" icon={Phone} />
              <EditableRow label="Correo Electrónico" field="email" icon={Mail} />
              <EditableRow label="Medio de Contacto" field="utm_medium" icon={Globe} />
            </div>
          </div>

          {/* WHATSAPP / NOTAS */}
          <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-2xl bg-green-500 text-white flex items-center justify-center shadow-lg shadow-green-200">
                <MessageCircle size={20} />
              </div>
              <div>
                <h2 className="text-lg font-serif text-slate-900">Conversación de Entrada</h2>
                <p className="text-xs text-slate-400 uppercase tracking-widest font-bold">Primer contacto vía WhatsApp</p>
              </div>
            </div>

            {lead.notas ? (
              <div className="relative">
                <div className="absolute -left-2 top-4 w-1 h-12 bg-green-500 rounded-full" />
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6 italic text-slate-600 leading-relaxed font-light">
                  "{lead.notas}"
                </div>
              </div>
            ) : (
              <div className="py-10 text-center border-2 border-dashed border-slate-100 rounded-3xl text-slate-300 italic text-sm">
                No hay mensaje inicial registrado.
              </div>
            )}
          </div>
        </div>

        {/* COLUMNA DERECHA: METADATOS */}
        <div className="space-y-6">
          <div className="bg-slate-900 text-white rounded-[2.5rem] p-8 shadow-xl shadow-slate-200">
            <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-400 mb-6 flex items-center gap-2">
              <Hash size={14} /> Atribución
            </h3>
            
            <div className="space-y-6">
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Canal de Origen</p>
                <p className="text-sm font-medium">{lead.origen_nombre || 'Directo'}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Fuente (UTM Source)</p>
                <p className="text-sm font-medium">{lead.utm_source || 'Sin fuente'}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Campaña</p>
                <p className="text-sm font-medium text-blue-400">{lead.utm_campaign || 'Orgánico'}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm">
            <h3 className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4">Pipeline</h3>
            <p className="text-sm font-serif text-slate-900 mb-2">Embudo asignado:</p>
            <p className="text-sm font-bold text-slate-700">{lead.pipeline_nombre || 'General'}</p>
          </div>
        </div>
      </div>
    </div>
  );
}